/**
 * useTimezone — lifted timezone state for the public booking page.
 *
 * Returns the currently-selected IANA timezone for displaying slots and
 * dates, plus a setter that persists the booker's choice in sessionStorage
 * for the current tab.
 * Initialization order:
 *   1. sessionStorage (user-picked override within the same tab/session)
 *   2. Browser's detected IANA timezone (Intl.DateTimeFormat) — used on
 *      every fresh page load when no same-tab override exists
 *   3. "UTC" as a last-resort fallback when Intl is unavailable
 *
 * A user-picked override does NOT survive closing the tab. Each new tab
 * or window starts fresh with the browser-detected timezone.
 *
 * The TZ also drives the availability fetch (passed as the `timezone` query
 * parameter to the backend) and the slot-time display, so changes propagate
 * end-to-end without prop-drilling.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'ahoy.booking.timezone';

/**
 * Distinct from `getBrowserTimezone` (`@/lib/scheduling/timezone`), which
 * seeds a one-time timezone-picker default during onboarding. This one is
 * sessionStorage-backed and scoped to the anonymous public booking page,
 * persisting a booker's in-tab timezone override across re-renders. Neither
 * is a duplicate of the other, and neither reintroduces the deleted
 * per-proposal timezone selector.
 */
export function detectBrowserTimezone(): string {
  if (typeof Intl === 'undefined') {
    return 'UTC';
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Confirm an IANA timezone string is recognised by the runtime BEFORE we let
 * it propagate into `Intl.DateTimeFormat({ timeZone })` calls — those throw
 * `RangeError: Invalid time zone specified` synchronously, and the booking
 * page formats with the timezone in dozens of places (every slot label,
 * calendar header, host card caption). A single bad sessionStorage value
 * (e.g. a deprecated zone like `Asia/Calcutta` against a runtime that only
 * knows `Asia/Kolkata`, or a corrupted value pasted into devtools) would
 * white-screen the entire booking surface.
 *
 * `Intl.DateTimeFormat({ timeZone: tz })` is the cheapest reliable probe:
 * it throws on invalid zones, returns a formatter for valid ones, and
 * mirrors the exact validation the downstream call sites perform.
 */
function isValidTimezone(tz: string): boolean {
  if (typeof Intl === 'undefined' || !tz) {
    return false;
  }
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function readStoredTimezone(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw && isValidTimezone(raw)) {
      return raw;
    }
    // Silently drop the bad value so subsequent reads stop fighting it.
    if (raw) {
      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function useTimezone(): {
  timezone: string;
  setTimezone: (next: string) => void;
  /** All IANA timezones the runtime can format in. Sorted alphabetically. */
  availableTimezones: readonly string[];
} {
  const [timezone, setTimezoneState] = useState<string>(() => {
    return readStoredTimezone() || detectBrowserTimezone();
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.sessionStorage.setItem(STORAGE_KEY, timezone);
    } catch {
      // sessionStorage can throw in sandboxed iframes / privacy modes
    }
  }, [timezone]);

  const setTimezone = useCallback((next: string) => {
    setTimezoneState(next);
  }, []);

  const availableTimezones = useMemo(() => {
    // Intl.supportedValuesOf is widely available (Chrome 99+, Safari 15.4+,
    // Firefox 93+). Fall back to a small curated list when missing.
    type IntlWithSupportedValues = typeof Intl & {
      supportedValuesOf?: (key: string) => string[];
    };
    const { supportedValuesOf } = Intl as IntlWithSupportedValues;
    if (typeof supportedValuesOf === 'function') {
      try {
        return [...supportedValuesOf('timeZone')].sort();
      } catch {
        // fall through
      }
    }
    return [
      'UTC',
      'Europe/London',
      'Europe/Dublin',
      'Europe/Paris',
      'Europe/Berlin',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'Asia/Tokyo',
      'Asia/Singapore',
      'Australia/Sydney',
      'Pacific/Auckland',
    ];
  }, []);

  return { timezone, setTimezone, availableTimezones };
}
